"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { Pencil } from "lucide-react";
import { getCurrentUserAction } from "@/actions/authActions";
import { fetchBlogBySlugAction } from "@/actions/blogActions";
import { fetchEventBySlugAction } from "@/actions/eventActions";
import { fetchStaticPageBySlugAction } from "@/actions/staticPagesActions";
import { fetchStoreWithCouponsBySlugAction } from "@/actions/storeActions";

interface EditTarget {
  label: string;
  href: string;
}

export default function AdminEditToolbar() {
  const pathname = usePathname();
  const [target, setTarget] = useState<EditTarget | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function resolveTarget() {
      setTarget(null);
      try {
        const user = await getCurrentUserAction();
        const permissions = new Set(user?.permissions || []);
        if (!user || permissions.size === 0) return;

        const segments = pathname.split("/").filter(Boolean).map(decodeURIComponent);
        let nextTarget: EditTarget | null = null;

        if (!segments.length && permissions.has("home")) {
          nextTarget = { label: "Open Admin Dashboard", href: "/admin" };
        } else if (!segments.length && permissions.has("hero-content")) {
          nextTarget = { label: "Open Admin Dashboard", href: "/admin" };
        } else if (segments[0] === "stores" && permissions.has("stores")) {
          if (segments[1]) {
            const result = await fetchStoreWithCouponsBySlugAction(segments[1]);
            if (result.data?._id) {
              nextTarget = {
                label: "Edit Store",
                href: `/admin/stores/edit/${encodeURIComponent(result.data.slug)}`,
              };
            }
          } else {
            nextTarget = { label: "Manage Stores", href: "/admin/stores" };
          }
        } else if (segments[0] === "blogs" && permissions.has("blog")) {
          if (segments[1]) {
            const result = await fetchBlogBySlugAction(segments[1]);
            if (result.data?._id) {
              nextTarget = {
                label: "Edit Blog",
                href: `/admin/blogs/edit/${result.data._id}`,
              };
            }
          } else {
            nextTarget = { label: "Manage Blogs", href: "/admin/blogs" };
          }
        } else if (segments[0] === "events" && permissions.has("events")) {
          if (segments[1]) {
            const result = await fetchEventBySlugAction(segments[1]);
            if (result.data?._id) {
              nextTarget = {
                label: "Edit Event",
                href: `/admin/events/edit/${result.data._id}`,
              };
            }
          } else {
            nextTarget = { label: "Manage Events", href: "/admin/events" };
          }
        } else if (segments[0] === "coupons" && permissions.has("coupons")) {
          nextTarget = { label: "Manage Coupons", href: "/admin/coupons" };
        } else if (
          segments.length === 1 &&
          permissions.has("staticpages") &&
          !["categories", "contactus", "coupons", "events", "stores", "blogs"].includes(segments[0])
        ) {
          const result = await fetchStaticPageBySlugAction(segments[0]);
          if (result.data?._id) {
            nextTarget = {
              label: "Edit Page",
              href: `/admin/staticpages/edit/${result.data._id}`,
            };
          }
        }

        if (!cancelled) setTarget(nextTarget);
      } catch {
        if (!cancelled) setTarget(null);
      }
    }

    void resolveTarget();
    return () => {
      cancelled = true;
    };
  }, [pathname]);

  if (!target) return null;

  return (
    <div className="sticky top-0 z-[60] flex min-h-10 items-center justify-end bg-[#17191f] px-4 text-xs text-white sm:px-6">
      <a
          href={target.href}
        className="inline-flex min-h-10 items-center gap-2 py-3 text-white/90 transition hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
        >
          <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
          {target.label}
      </a>
    </div>
  );
}